import React from 'react';

/**
 * Password-strength meter — 4-bar visual + label + actionable hints.
 *
 * Pure function of the password string. No state of its own. Drop-in below any
 * password input. Matches the look of ParentSetup.js (the original author),
 * so all sign-up flows have a consistent feel.
 *
 * Strength rubric (0–4 bars):
 *   1. ≥ 8 characters
 *   2. mixed-case letters
 *   3. at least one digit
 *   4. at least one symbol
 */

export function calculatePasswordStrength(pwd = '') {
  if (!pwd) return 0;
  let strength = 0;
  if (pwd.length >= 8) strength++;
  if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) strength++;
  if (/\d/.test(pwd)) strength++;
  if (/[^a-zA-Z0-9]/.test(pwd)) strength++;
  return strength;
}

const COLORS = ['bg-gray-300', 'bg-red-500', 'bg-yellow-500', 'bg-blue-500', 'bg-emerald-500'];
const LABELS = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'];
const LABEL_COLORS = ['#9CA3AF', '#EF4444', '#F59E0B', '#3B82F6', '#10B981'];

const HINTS_BY_STRENGTH = (pwd) => {
  const hints = [];
  if (pwd.length < 8) hints.push('Use at least 8 characters');
  if (!(/[a-z]/.test(pwd) && /[A-Z]/.test(pwd))) hints.push('Mix upper- and lower-case letters');
  if (!/\d/.test(pwd)) hints.push('Add a number');
  if (!/[^a-zA-Z0-9]/.test(pwd)) hints.push('Add a symbol (e.g. ! @ # $)');
  return hints;
};

export const PasswordStrengthMeter = ({ password = '', showHints = true, testIdPrefix = 'pwd-strength' }) => {
  if (!password) return null;
  const strength = calculatePasswordStrength(password);
  const color = COLORS[strength];
  const label = LABELS[strength];
  const labelColor = LABEL_COLORS[strength];
  const hints = HINTS_BY_STRENGTH(password);

  return (
    <div className="mt-2" data-testid={testIdPrefix}>
      <div className="flex gap-1 mb-1">
        {[1, 2, 3, 4].map((level) => (
          <div
            key={level}
            className={`h-1 flex-1 rounded transition-colors ${strength >= level ? color : 'bg-gray-200'}`}
            data-testid={`${testIdPrefix}-bar-${level}`}
          />
        ))}
      </div>
      <p
        className="text-xs font-medium"
        style={{ color: labelColor }}
        data-testid={`${testIdPrefix}-label`}
      >
        {label}
      </p>
      {showHints && hints.length > 0 && strength < 4 && (
        <ul className="text-[11px] text-gray-500 mt-1 space-y-0.5" data-testid={`${testIdPrefix}-hints`}>
          {hints.slice(0, 2).map((h) => (
            <li key={h}>· {h}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PasswordStrengthMeter;
